// src/routes/RootRedirect.tsx
//
// Landing target for "/". Picks the right module to send the user to based
// on their context:
//   1. The last module they used (localStorage, via ModulesContext).
//   2. The first available (non-coming-soon) module.
//   3. `config` as a last resort.
//
// This runs inside the <ModulesProvider> tree, so `availableModules` is
// already filtered by user role + backend module flags.

import { Navigate } from 'react-router-dom';

import { useModules } from '@/context/ModulesContext';
import { useDashboardShell } from '@/context/DashboardShellContext';
import { defaultTabOf } from '@/modules';

export function RootRedirect() {
  const { loading, defaultModuleKey, availableModules, isAdmin, isDev } = useModules();
  const { shell, config: shellConfig, loading: shellLoading } = useDashboardShell();

  // chat.ucognet.pro (y cualquier host `chat.*`) aterriza en la interfaz tipo
  // ChatGPT full-screen (/chat), no en el dashboard de módulos.
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('chat.')) {
    return <Navigate to="/chat" replace />;
  }

  if (loading || shellLoading) {
    return (
      <div className="p-10 text-center text-white/60">Cargando módulos…</div>
    );
  }

  // Shell «Orion» (opt-in por datos): la puerta es su Inicio.
  // Con `home: false` la org conserva su puerta de siempre (sigue abajo).
  if (shell === 'orion' && shellConfig?.home !== false && availableModules.some((m) => m.key === 'orion')) {
    return <Navigate to="/m/orion/inicio" replace />;
  }

  const key = defaultModuleKey();

  // 🚨 2026-07-22 · La puerta del empleado.
  //
  // `defaultModuleKey()` devuelve el PRIMER módulo disponible del registry, y
  // los core (empresa/config) van primero y son SIEMPRE visibles — no tienen
  // `backendModuleKey`. Para un admin está bien; para un EMPLEADO significa
  // aterrizar en la administración de la empresa y ver "Insufficient
  // permissions", porque el backend le niega esa lectura (con razón).
  //
  // Pasó de verdad: se mandaron correos con accesos del diagnóstico operativo
  // y la gente llegaba a una pantalla de error en vez de a su entrevista.
  //
  // ⚠️ Un intento anterior condicionó a `availableModules.length === 0` y NUNCA
  // se activó, justamente porque los core siempre están ahí. Lo que distingue a
  // un empleado es no tener acceso a la administración, no la lista vacía.
  //
  // Si no es admin ni dev y tiene algún módulo de PRODUCTO, ahí va — su trabajo
  // está en ese módulo, no en la configuración de la empresa.
  if (!isAdmin && !isDev) {
    const suyo = availableModules.find(
      (m) => !!m.backendModuleKey && !m.comingSoon &&
             m.sidebarItems.some((i) => !i.comingSoon),
    );
    if (suyo) {
      const t = defaultTabOf(suyo.key);
      return <Navigate to={t ? `/m/${suyo.key}/${t}` : `/m/${suyo.key}`} replace />;
    }
    // Sin módulos de producto: el chat es mejor puerta que un 403.
    if (key === 'config' || key === 'empresa') {
      return <Navigate to="/chat" replace />;
    }
  }

  const tab = defaultTabOf(key);
  return <Navigate to={tab ? `/m/${key}/${tab}` : `/m/${key}`} replace />;
}

export default RootRedirect;
